import React, { useState, useEffect } from 'react';
import { alertesAPI, apprenantsAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import Icon from '../components/Icon';
import Modal from '../components/Modal';

const Alertes = () => {
  const navigate = useNavigate();
  const [allApprenants, setAllApprenants] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  // État unique pour le modal
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalType, setModalType] = useState('');
  const [modalApprenants, setModalApprenants] = useState([]);
  const [modalSearch, setModalSearch] = useState('');
  const [modalPage, setModalPage] = useState(1);
  const modalPerPage = 10;

  useEffect(() => {
    fetchAlertes();
    fetchAllApprenants();
  }, []);

  const fetchAllApprenants = async () => {
    try {
      const response = await apprenantsAPI.getAll({ limit: 1000 });
      setAllApprenants(response.data.apprenants || []);
    } catch (error) {
      console.error('Erreur chargement apprenants:', error);
    }
  };

  const fetchAlertes = async () => {
    try {
      const response = await alertesAPI.getAll();
      const alertesData = response.data.alertes || [];
      setAlertes(alertesData);
      setCounts(response.data.counts || {});
    } catch (error) {
      toast.error('Erreur lors du chargement des alertes');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour ouvrir le modal avec le type d'alerte sélectionné
  const openModal = (type) => {
    setModalType(type);
    setModalPage(1);

    // Définir le titre selon le type
    const titles = {
      critique: 'Alertes Critiques',
      relance: 'Relances nécessaires',
      suivi: 'Suivis 3 mois requis',
      verif: 'Vérifications nécessaires'
    };
    setModalTitle(titles[type] || 'Alertes');

    // Récupérer les alertes du type sélectionné
    const alertesType = alertes.filter(a => a.type === type);

    // Récupérer les IDs uniques des apprenants
    const apprenantsIds = [...new Set(alertesType.map(a => a.apprenantId))];

    // Filtrer depuis les apprenants déjà chargés en mémoire (pas d'appel réseau)
    const apprenantsData = allApprenants.filter(a => apprenantsIds.includes(a._id));
    setModalApprenants(apprenantsData);

    setShowModal(true);
  };

  // Fonction pour obtenir le statut de suivi
  const getStatutSuivi = (apprenant) => {
    return apprenant.dernierNiveauSuivi || 'Non suivi';
  };

  // Fonction pour obtenir la couleur du badge de statut
  const getStatutColor = (statut) => {
    const colors = {
      'M+6': 'bg-green-100 text-green-800',
      'M+3': 'bg-amber-100 text-amber-800',
      'M+0': 'bg-blue-100 text-blue-800',
      'Non suivi': 'bg-gray-100 text-gray-600'
    };
    return colors[statut] || 'bg-gray-100 text-gray-600';
  };

  const exportAlertes = () => {
    const data = alertes.map(a => ({
      Type: a.type,
      Titre: a.titre,
      Message: a.msg || a.message,
      Apprenant: a.apprenantNom || '-'
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Alertes');
    XLSX.writeFile(wb, `SIMPLON_Alertes_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
    toast.success('Alertes exportées !');
  };

  const getTypeColor = (type) => {
    const colors = {
      critique: 'bg-red-50 border-red-200 text-red-800',
      relance: 'bg-amber-50 border-amber-200 text-amber-800',
      suivi: 'bg-blue-50 border-blue-200 text-blue-800',
      verif: 'bg-purple-50 border-purple-200 text-purple-800'
    };
    return colors[type] || 'bg-gray-50 border-gray-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }
  const filteredModalApprenants = modalApprenants.filter(a => {
    if (!modalSearch.trim()) return true;
    const s = modalSearch.trim().toLowerCase();
    return (
      a.nom?.toLowerCase().includes(s) ||
      a.prenom?.toLowerCase().includes(s) ||
      a.telephone?.toLowerCase().includes(s) ||
      a.typeFormation?.toLowerCase().includes(s)
    );
  });

  const modalTotalPages = Math.ceil(modalApprenants.length / modalPerPage);
  const modalPaginatedApprenants = modalApprenants.slice(
    (modalPage - 1) * modalPerPage,
    modalPage * modalPerPage
  );

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 flex items-center text-gray-800">
        <Icon name="alertes" size={28} className="mr-3 text-red-600" />
        Alertes
      </h2>

      {/* KPIs cliquables */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {/* Critiques */}
        <div
          className="kpi-card section-card bg-white border-l-4 border-red-600 p-5 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openModal('critique')}
        >
          <div className="flex items-center justify-between mb-2">
            <Icon name="error" size={24} color="#dc2626" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Critiques</span>
          </div>
          <p className="text-3xl font-bold text-gray-800">{counts.critique || 0}</p>
        </div>

        {/* Relances */}
        <div
          className="kpi-card section-card bg-white border-l-4 border-amber-500 p-5 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openModal('relance')}
        >
          <div className="flex items-center justify-between mb-2">
            <Icon name="warning" size={24} color="#f59e0b" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Relances</span>
          </div>
          <p className="text-3xl font-bold text-gray-800">{counts.relance || 0}</p>
        </div>

        {/* Suivi 3 mois */}
        <div
          className="kpi-card section-card bg-white border-l-4 border-blue-500 p-5 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openModal('suivi')}
        >
          <div className="flex items-center justify-between mb-2">
            <Icon name="phone" size={24} color="#3b82f6" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Suivi 3 mois</span>
          </div>
          <p className="text-3xl font-bold text-gray-800">{counts.suivi || 0}</p>
        </div>

        {/* Vérifications */}
        <div
          className="kpi-card section-card bg-white border-l-4 border-purple-500 p-5 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openModal('verif')}
        >
          <div className="flex items-center justify-between mb-2">
            <Icon name="view" size={24} color="#8b5cf6" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Vérifications</span>
          </div>
          <p className="text-3xl font-bold text-gray-800">{counts.verif || 0}</p>
        </div>
      </div>

      {/* Liste des Alertes */}
      <div className="section-card bg-white rounded-xl p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold flex items-center text-gray-800">
            <Icon name="liste" size={20} className="mr-2 text-red-600" />
            Toutes les Alertes
          </h3>
          <button
            onClick={exportAlertes}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
          >
            <Icon name="download" size={18} />
            <span>Exporter</span>
          </button>
        </div>
        <div className="space-y-3 max-h-[500px] overflow-y-auto">
          {alertes.length > 0 ? (
            alertes.map((alerte, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border-2 ${getTypeColor(alerte.type)}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name={alerte.type === 'critique' ? 'error' : alerte.type === 'relance' ? 'warning' : 'info'} size={20} className={alerte.type === 'critique' ? 'text-red-600' : alerte.type === 'relance' ? 'text-amber-600' : 'text-blue-600'} />
                      <h4 className="font-semibold">{alerte.titre}</h4>
                    </div>
                    <p className="text-sm opacity-90">{alerte.message || alerte.msg}</p>
                    {alerte.apprenantNom && (
                      <p className="text-xs mt-2 opacity-75">
                        Apprenant: {alerte.apprenantNom}
                      </p>
                    )}
                  </div>
                  {alerte.apprenantId && (
                    <button
                      onClick={() => navigate('/liste')}
                      className="ml-4 flex items-center gap-1 px-3 py-1 bg-white bg-opacity-50 rounded-lg text-sm hover:bg-opacity-75 transition-colors"
                    >
                      <Icon name="view" size={16} />
                      <span>Voir</span>
                    </button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-gray-500 py-8 flex flex-col items-center">
              <Icon name="success" size={48} className="text-green-500 mb-2" />
              <p>Aucune alerte</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal UNIQUE avec contenu dynamique */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={`${modalTitle} (${modalApprenants.length})`}
        size="xl"
      >
        <div>
          {modalApprenants.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">Nom</th>
                      <th className="px-4 py-3 text-left">Prénom</th>
                      <th className="px-4 py-3 text-left">Téléphone</th>
                      <th className="px-4 py-3 text-left">Formation</th>
                      <th className="px-4 py-3 text-left">Statut de suivi</th>
                      <th className="px-4 py-3 text-left">Date dernier suivi</th>
                      <th className="px-4 py-3 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {modalPaginatedApprenants.map((apprenant) => {
                      const statut = getStatutSuivi(apprenant);
                      return (
                        <tr key={apprenant._id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">{apprenant.nom || '-'}</td>
                          <td className="px-4 py-3">{apprenant.prenom || '-'}</td>
                          <td className="px-4 py-3">{apprenant.telephone || '-'}</td>
                          <td className="px-4 py-3">{apprenant.typeFormation || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatutColor(statut)}`}>
                              {statut}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {apprenant.dateDernierSuivi ? format(new Date(apprenant.dateDernierSuivi), 'dd/MM/yyyy') : '-'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => {
                                setShowModal(false);
                                navigate('/liste');
                              }}
                              className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                            >
                              Voir
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {modalTotalPages > 1 && (
                <div className="mt-4 flex justify-between items-center border-t pt-3">
                  <p className="text-sm text-gray-500">
                    {modalApprenants.length} entrée{modalApprenants.length > 1 ? 's' : ''}
                    {' '}— page {modalPage}/{modalTotalPages}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setModalPage(p => Math.max(1, p - 1))}
                      disabled={modalPage === 1}
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 text-sm"
                    >◀</button>
                    <span className="px-3 py-1 bg-gray-100 rounded text-sm">
                      {modalPage} / {modalTotalPages}
                    </span>
                    <button
                      onClick={() => setModalPage(p => Math.min(modalTotalPages, p + 1))}
                      disabled={modalPage >= modalTotalPages}
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 text-sm"
                    >▶</button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-gray-500 py-8 flex flex-col items-center">
              <Icon name="success" size={48} className="text-green-500 mb-2" />
              <p>Aucun apprenant dans cette catégorie</p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Alertes;